/**
 * V4 device properties shared model.
 *
 * V4 devices expose a unified `properties` map (system + custom) that replaces
 * the v3 first-class fields like phone / industry / alarmProtocol. The system
 * fields are well-known and always present, with an `inputHelper` hint for the
 * UI. Custom fields are free-form name/value pairs (no helper).
 *
 * On the v3 model side they live in `extraFields` so the existing UI patterns
 * (Autocomplete + TextField + add/delete) can be reused.
 */

import { phoneOrEmpty } from '../phone';

export const V4_PROPERTY_HELPERS = {
  PHONE_NUMBER: 'phone_number',
  ADDRESS: 'address',
  ALARM_PROTOCOL: 'alarm_protocol',
};

// Canonical list of system properties surfaced on every v4 device. Used to
// seed the UI when the backend response omits a system entry, so users can
// always set values without needing the backend to pre-create the row.
export const V4_SYSTEM_PROPERTIES = [
  { name: 'phone_number', inputHelper: V4_PROPERTY_HELPERS.PHONE_NUMBER },
  { name: 'custom_address', inputHelper: V4_PROPERTY_HELPERS.ADDRESS },
  { name: 'operating_address', inputHelper: V4_PROPERTY_HELPERS.ADDRESS },
  { name: 'industry', inputHelper: '' },
  { name: 'alarm_protocol', inputHelper: V4_PROPERTY_HELPERS.ALARM_PROTOCOL },
];

const SYSTEM_NAME_SET = new Set(V4_SYSTEM_PROPERTIES.map((p) => p.name));

export const isV4SystemPropertyName = (name) => SYSTEM_NAME_SET.has(name);

// System property name -> the v3 translation key labelling the same field.
export const V4_SYSTEM_PROPERTY_LABEL_KEYS = {
  phone_number: 'phone',
  custom_address: 'tempAddress',
  operating_address: 'operatingAddress',
  industry: 'industry',
  alarm_protocol: 'alarmProtocol',
};

const humanizePropertyName = (name) =>
  String(name || '')
    .split('_')
    .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : s))
    .join(' ');

// Falls back to humanising system properties the backend adds later.
export const v4SystemPropertyLabel = (t, name) => {
  const key = V4_SYSTEM_PROPERTY_LABEL_KEYS[name];
  return key ? t(key) : humanizePropertyName(name);
};

const isPhoneField = (inputHelper) =>
  inputHelper === V4_PROPERTY_HELPERS.PHONE_NUMBER;

const systemField = (name, entry, inputHelper) => {
  const stored = entry?.value || '';
  const value = isPhoneField(inputHelper) ? phoneOrEmpty(stored) : stored;
  return {
    name,
    value,
    inputHelper,
    system: true,
    // Reads as empty, but the next save still has to clear it on the server.
    ...(value !== stored ? { storedValue: stored } : {}),
  };
};

/**
 * Flatten a V4 device's `properties` payload into the v3 `extraFields` shape.
 * System fields keep `system: true` and their inputHelper; custom fields are
 * `system: false`. System fields missing from the backend response are seeded
 * with empty values so the UI always shows the canonical list.
 */
export function v4PropertiesToExtraFields(properties) {
  const system = properties?.system || {};
  const custom = properties?.custom || {};

  const fields = V4_SYSTEM_PROPERTIES.map(({ name, inputHelper }) =>
    systemField(name, system[name], system[name]?.inputHelper || inputHelper)
  );

  // Append any system entries returned by the API that aren't in the canonical
  // list (forward compat with new system fields).
  Object.entries(system).forEach(([name, entry]) => {
    if (SYSTEM_NAME_SET.has(name)) return;
    fields.push(systemField(name, entry, entry?.inputHelper || ''));
  });

  Object.entries(custom).forEach(([name, entry]) => {
    fields.push({
      name,
      value: entry?.value || '',
      inputHelper: '',
      system: false,
    });
  });

  return fields;
}

/**
 * System properties mapV4DeviceToV3 also surfaces as first-class v3 fields.
 * The form edits only `extraFields`, so a save has to recompute these.
 */
export const V4_DERIVED_V3_FIELDS = {
  operating_address: 'operatingAddress',
  custom_address: 'tempAddress',
};

export function v4DerivedV3Fields(extraFields = []) {
  const byName = new Map((extraFields || []).map((f) => [f.name, f]));
  const derived = {};
  Object.entries(V4_DERIVED_V3_FIELDS).forEach(([property, field]) => {
    derived[field] = byName.get(property)?.value || '';
  });
  return derived;
}

// Drops storedValue too: once saved, there is nothing left to clear.
export function clearBareDialCodes(extraFields) {
  return (extraFields || []).map(({ storedValue, ...field }) =>
    isPhoneField(field.inputHelper)
      ? { ...field, value: phoneOrEmpty(field.value) }
      : field
  );
}

/**
 * Diff old vs new `extraFields` for a v4 device. Returns the set of changes
 * the saga needs to apply via the per-property POST/DELETE endpoints.
 *
 *   upserts: properties whose value changed or that were newly added
 *   deletes: custom properties that were removed (system fields are never
 *            deleted — clearing their value is a POST with empty string)
 */
export function diffV4ExtraFields(oldFields = [], newFields = []) {
  const oldByName = new Map(oldFields.map((f) => [f.name, f]));
  const newByName = new Map(newFields.map((f) => [f.name, f]));

  const upserts = [];
  newFields.forEach((f) => {
    if (!f.name) return;
    const prev = oldByName.get(f.name);
    if (!prev || (prev.storedValue ?? prev.value) !== f.value) {
      upserts.push({ name: f.name, value: f.value || '' });
    }
  });

  const deletes = [];
  oldFields.forEach((f) => {
    if (f.system) return; // system fields stay; clearing value is an upsert
    if (!newByName.has(f.name)) deletes.push({ name: f.name });
  });

  return { upserts, deletes };
}
